import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { Upload, Form, message, Button } from 'antd';
import UploadedFile from 'components/SendItems/UploadedFile';
import { chunkUploadMedia, downloadBlob, idCardRequired } from 'utils';
import { fileDownload } from 'fetcher';
import { useSendArticleContentMutation } from 'services';

const mediaFileType = '.docx, .txt';

const UploadText = () => (
  <>
    <p className="ant-upload-drag-icon">
      <InboxOutlined />
    </p>
    <p className="ant-upload-text">Dosyayı sürükleyip bırakın ya da tıklayın</p>
    <p className="ant-upload-hint">Dosya boyutu 25MB'ı geçmemelidir.</p>
  </>
);

/**
 * @description Madde dosyası ve kimlik kartı yükleme bloğu. Yüklenen dosya maddeye ait tek ortak
 * dosyadır; üzerine yeniden yükleme yapıldığında backend yeni bir sürüm üretir, önceki sürüm silinmez.
 *
 * @param {Object} props
 * @param {number|string} props.id Madde teklifi ID'si.
 * @param {number} [props.articleTypeID] Madde türü ID'si; kimlik kartının zorunlu olup olmadığını belirler.
 * @param {boolean} [props.allowItemFileDownload=false] Yüklü madde dosyasının yanında indirme butonu
 *   gösterilsin mi. Yalnız dosyayı okumak zorunda olan aşamalar için açılır (tercüme aşamasında kaynak
 *   metin indirilmeden çeviri yapılamaz); yazar ve madde sorumlusu ekranlarında kapalı kalır.
 *   Buton açıkken silme butonunun yerini alır — dosya zaten o aşamalarda silinemez durumdadır.
 *   Kimlik kartını etkilemez.
 */
const ContentUpload = ({ id, articleTypeID, allowItemFileDownload = false }) => {
  const form = Form.useFormInstance();
  const [sendArticleContent] = useSendArticleContentMutation();
  const isIdCardRequired = idCardRequired.includes(articleTypeID) || false;

  const itemFile = Form.useWatch('itemFile', form);
  const idCard = Form.useWatch('idCard', form);

  const handleChange = (info) => {
    if (info.file.status === 'done') {
      message.destroy();
      message.success('Dosya yüklendi');
    } else if (info.file.status === 'error') {
      message.destroy();
      message.error('Dosya yüklenirken bir hata oluştu');
    } else if (info.file.status === 'uploading') {
      message.loading('Dosya yükleniyor', 0);
    }
  };
  // draggable alanda tiklama dosya secme penceresini actigi icin indirme butonu olayi durdurur
  const handleDownload = async (e, media) => {
    e.stopPropagation();
    const { data } = await fileDownload(media.fileFullUrl);
    downloadBlob(data, media.name);
  };

  // dosya yükleme işlemi customUpload funksiyonu
  const handleCustomUpload = async ({ file, onSuccess, onError, type, formItemName }) => {
    try {
      const [res] = await chunkUploadMedia('articleContent', [file]);
      const { error } = await sendArticleContent({
        offerID: id,
        body: {
          media: [`/api/media/${res.media.id}`],
          type,
        },
      });
      if (error) {
        form.resetFields([formItemName]);
        onError('error');
      }
      onSuccess('done');
    } catch (error) {
      form.resetFields([formItemName]);
      onError('error');
    }
  };

  return (
    <>
      <Form.Item
        label="Madde Dosyası"
        className="gx-mb-4"
        name="itemFile"
        required
        // Upload `fileList` bekler; Form.Item'ın enjekte ettiği geçersiz `value`
        // prop'unu engelle (görsel useWatch + showUploadList={false} ile yönetiliyor)
        getValueProps={() => ({})}
        rules={[
          {
            required: true,
            message: 'Lütfen madde dosyasını yükleyiniz',
          },
        ]}
      >
        <Upload.Dragger
          maxCount={1}
          onChange={handleChange}
          accept={mediaFileType}
          customRequest={({ file, onSuccess, onError }) =>
            handleCustomUpload({
              file,
              onSuccess,
              onError,
              type: 0,
              formItemName: 'itemFile',
            })
          }
          showUploadList={false}
        >
          <>
            {itemFile?.media?.[0] ? (
              <UploadedFile
                content={itemFile?.media[0]?.name}
                id={itemFile.id}
                offerID={id}
                disableDelete={itemFile && !itemFile.isDeletable}
                extra={
                  allowItemFileDownload && (
                    <Button
                      type="link"
                      size="large"
                      style={{ marginBottom: 0 }}
                      onClick={(e) => handleDownload(e, itemFile.media[0])}
                      icon={<DownloadOutlined />}
                    />
                  )
                }
              />
            ) : (
              <UploadText />
            )}
          </>
        </Upload.Dragger>
      </Form.Item>
      <Form.Item
        label="Kimlik Kartı"
        className="gx-mb-4"
        name="idCard"
        getValueProps={() => ({})}
        rules={[
          {
            required: isIdCardRequired,
            message: 'Lütfen kimlik kartınızı yükleyiniz',
          },
        ]}
      >
        <Upload.Dragger
          maxCount={1}
          onChange={handleChange}
          accept={mediaFileType}
          customRequest={({ file, onSuccess, onError }) =>
            handleCustomUpload({
              file,
              onSuccess,
              onError,
              type: 1,
              formItemName: 'idCard',
            })
          }
          showUploadList={false}
        >
          <>
            {idCard?.media?.[0] ? (
              <UploadedFile
                content={idCard?.media[0]?.name}
                id={idCard.id}
                offerID={id}
                disableDelete={idCard && !idCard.isDeletable}
              />
            ) : (
              <UploadText />
            )}
          </>
        </Upload.Dragger>
      </Form.Item>
    </>
  );
};

export default ContentUpload;
